import { useState, useEffect, useRef, useCallback } from 'react'
import { useNavigate } from '@tanstack/react-router'
import { Search, X } from 'lucide-react'
import { useGlobalSearch } from '@/hooks/use-global-search'
import { SearchResults } from './search-results'
import {
  InputGroup,
  InputGroupAddon,
  InputGroupInput,
} from '../ui/input-group'
import { Kbd } from '../ui/kbd'
import {
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from '../ui/sidebar'

const placeholders = [
  'Find an invoice',
  'Find a lead',
  'Find a customer',
  'Find anything really lol'
]

/**
 * Global search, pinned to the top of the app sidebar. Expanded, it is a
 * rounded input whose results float out over the content card; in the
 * collapsed icon rail it is a search icon that expands the sidebar and focuses
 * the input. ⌘K does the same from anywhere.
 */
export function SidebarSearch() {
  const navigate = useNavigate()
  const { state, isMobile, setOpen } = useSidebar()
  const [placeholderIndex, setPlaceholderIndex] = useState(0)
  const [searchQuery, setSearchQuery] = useState('')
  const [debouncedQuery, setDebouncedQuery] = useState('')
  const [isSearchOpen, setIsSearchOpen] = useState(false)
  const [selectedIndex, setSelectedIndex] = useState(0)
  const [focusPending, setFocusPending] = useState(false)
  const searchInputRef = useRef<HTMLInputElement>(null)
  const searchContainerRef = useRef<HTMLDivElement>(null)

  const isCollapsed = state === 'collapsed' && !isMobile

  // Debounce search query
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedQuery(searchQuery)
    }, 300)
    return () => clearTimeout(timer)
  }, [searchQuery])

  // Global search hook
  // fetchEnabled starts loading contacts/leads/events/invoices as soon as the
  // search UI opens (input focus), so data is warm by the time the user has
  // typed 2 characters and `enabled` flips the fuzzy-filtering on.
  const { results, isLoading: searchLoading } = useGlobalSearch({
    query: debouncedQuery,
    enabled: isSearchOpen && debouncedQuery.length >= 2,
    fetchEnabled: isSearchOpen,
  })

  // Reset selected index when results change
  useEffect(() => {
    setSelectedIndex(0)
  }, [debouncedQuery])

  // Get all results as flat array for keyboard navigation
  const getAllResults = useCallback(() => {
    return [
      ...results.contacts,
      ...results.leads,
      ...results.events,
      ...results.invoices,
    ]
  }, [results])

  const closeSearch = useCallback(() => {
    setIsSearchOpen(false)
    setSearchQuery('')
    setDebouncedQuery('')
    setSelectedIndex(0)
    searchInputRef.current?.blur()
  }, [])

  // Handle keyboard navigation
  const handleSearchKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      const allResults = getAllResults()

      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault()
          setSelectedIndex((prev) => Math.min(prev + 1, allResults.length - 1))
          break
        case 'ArrowUp':
          e.preventDefault()
          setSelectedIndex((prev) => Math.max(prev - 1, 0))
          break
        case 'Enter':
          e.preventDefault()
          if (allResults[selectedIndex]) {
            navigate({ to: allResults[selectedIndex].href })
            closeSearch()
          }
          break
        case 'Escape':
          e.preventDefault()
          closeSearch()
          break
      }
    },
    [getAllResults, selectedIndex, navigate, closeSearch]
  )

  // Close search on click outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        searchContainerRef.current &&
        !searchContainerRef.current.contains(e.target as Node)
      ) {
        setIsSearchOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  useEffect(() => {
    const interval = setInterval(() => {
      setPlaceholderIndex((prev) => (prev + 1) % placeholders.length)
    }, 3000) // Rotate every 3 seconds

    return () => clearInterval(interval)
  }, [])

  // The input is display:none in the icon rail, so it can only take focus once
  // the sidebar has re-rendered expanded.
  const focusSearch = useCallback(() => {
    if (isCollapsed) {
      setOpen(true)
      setFocusPending(true)
    } else {
      searchInputRef.current?.focus()
    }
  }, [isCollapsed, setOpen])

  useEffect(() => {
    if (focusPending && !isCollapsed) {
      searchInputRef.current?.focus()
      setFocusPending(false)
    }
  }, [focusPending, isCollapsed])

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault()
        focusSearch()
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [focusSearch])

  return (
    <>
      {/* Icon-rail stand-in: expands the sidebar and focuses the input. */}
      <SidebarMenu className="hidden group-data-[collapsible=icon]:flex">
        <SidebarMenuItem>
          <SidebarMenuButton tooltip="Search (⌘K)" onClick={focusSearch}>
            <Search />
            <span>Search</span>
          </SidebarMenuButton>
        </SidebarMenuItem>
      </SidebarMenu>

      <div ref={searchContainerRef} className="relative group-data-[collapsible=icon]:hidden">
        <InputGroup className="h-8 cursor-text rounded-lg border-sidebar-border bg-sidebar-accent shadow-none">
          <InputGroupAddon className="text-sidebar-foreground/60">
            <Search className="h-4 w-4" />
          </InputGroupAddon>
          <InputGroupInput
            ref={searchInputRef}
            type="search"
            aria-label="Search"
            placeholder={placeholders[placeholderIndex]}
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value)
              if (!isSearchOpen) setIsSearchOpen(true)
            }}
            onFocus={() => setIsSearchOpen(true)}
            onKeyDown={handleSearchKeyDown}
            className="text-sidebar-foreground placeholder:text-sidebar-foreground/50 [&::-webkit-search-cancel-button]:appearance-none [&::-webkit-search-cancel-button]:hidden"
          />
          <InputGroupAddon align="inline-end">
            {searchQuery ? (
              <button
                type="button"
                aria-label="Clear search"
                onClick={(e) => {
                  e.stopPropagation()
                  setSearchQuery('')
                  setDebouncedQuery('')
                  searchInputRef.current?.focus()
                }}
                className="text-sidebar-foreground/60 hover:text-sidebar-foreground transition-colors"
              >
                <X className="h-4 w-4" />
              </button>
            ) : (
              <Kbd className="hidden bg-sidebar-border text-sidebar-foreground/60 md:flex">⌘K</Kbd>
            )}
          </InputGroupAddon>
        </InputGroup>
        <SearchResults
          results={results}
          isLoading={searchLoading}
          isOpen={isSearchOpen && (searchQuery.length >= 2 || debouncedQuery.length >= 2)}
          onSelect={closeSearch}
          selectedIndex={selectedIndex}
          onSelectedIndexChange={setSelectedIndex}
          query={searchQuery}
          // The sidebar column is too narrow for result rows; float wider over
          // the content card on desktop.
          className="md:right-auto md:w-96"
        />
      </div>
    </>
  )
}
